import type { RootState } from '.';
import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type { SalesforceItem } from '../components/parser/ParserTypes';
import type { BaseElementProperties } from '@server/documentation';
import { createConfigurationItemFromParserResponseItem } from '../components/parser/parserUtils';
import {
  apexClassServiceNowMapping,
  apexTriggerServiceNowMapping,
  flowServiceNowMapping,
  permissionSetServiceNowMapping,
  profileServiceNowMapping,
} from '../components/pages/org-migration-demo/serviceNowMappingData';
import type { UserStory } from '../components/pages/org-migration-demo/types';

export interface ConfigurationItemForServiceNow {
  dependencyType?: string;
  overview: string;
  serviceNowConcept: string;
  serviceNowMapping?: string[];
  migrationConsiderations: string[];
  object?: string;
  userStory?: UserStory;
  status?: string;
}

export type EnrichedConfigurationItem = SalesforceItem & Partial<ConfigurationItemForServiceNow>;

export interface OrgMigrationMetadataItems {
  [metadataType: string]: EnrichedConfigurationItem[];
}

export interface OrgMigrationState {
  metadataByType: {
    [crmOrgId: string]: OrgMigrationMetadataItems;
  };
  activeSubTab: {
    [tabId: string]: string;
  };
  searchText: {
    [tabId: string]: string;
  };
}

const initialState: OrgMigrationState = {
  metadataByType: {},
  activeSubTab: {},
  searchText: {},
};

export const orgMigrationSlice = createSlice({
  name: 'orgMigration',
  initialState,
  reducers: {
    setMetadataForType: (
      state,
      action: PayloadAction<{
        crmOrgId: string;
        metadataType: string;
        data: BaseElementProperties[];
      }>,
    ) => {
      const { crmOrgId, metadataType, data } = action.payload;

      const configurationItems = data.map((item) => {
        const baseItem = createConfigurationItemFromParserResponseItem({
          responseItem: item,
          objectApiName: item.objectName ?? '',
          keyType: metadataType,
        });

        let serviceNowMapping = null;
        switch (metadataType) {
          case 'ApexClass':
            serviceNowMapping = apexClassServiceNowMapping;
            break;
          case 'ApexTrigger':
            serviceNowMapping = apexTriggerServiceNowMapping;
            break;
          case 'Flow':
            serviceNowMapping = flowServiceNowMapping;
            break;
          case 'PermissionSet':
            serviceNowMapping = permissionSetServiceNowMapping;
            break;
          case 'Profile':
            serviceNowMapping = profileServiceNowMapping;
            break;
        }

        if (serviceNowMapping) {
          const serviceNowKey = `${baseItem.name}.${metadataType}`;
          const serviceNowData = serviceNowMapping[serviceNowKey];

          if (serviceNowData) {
            return {
              ...baseItem,
              overview: serviceNowData.overview,
              serviceNowConcept: serviceNowData.serviceNowConcept,
              migrationConsiderations: serviceNowData.migrationConsiderations,
              ...(serviceNowData.dependencyType && {
                dependencyType: serviceNowData.dependencyType,
              }),
              ...(serviceNowData.serviceNowMapping && {
                serviceNowMapping: serviceNowData.serviceNowMapping,
              }),
              ...(serviceNowData.object && { object: serviceNowData.object }),
              ...(serviceNowData.userStory && { userStory: serviceNowData.userStory }),
              ...(serviceNowData.status && { status: serviceNowData.status }),
            };
          }
        }

        return baseItem;
      });

      if (!state.metadataByType[crmOrgId]) {
        state.metadataByType[crmOrgId] = {};
      }
      state.metadataByType[crmOrgId][metadataType] = configurationItems;
    },
    setActiveSubTab: (
      state,
      action: PayloadAction<{
        tabId: string;
        subTabId: string;
      }>,
    ) => {
      const { tabId, subTabId } = action.payload;
      state.activeSubTab[tabId] = subTabId;
    },
    setSearchText: (
      state,
      action: PayloadAction<{
        tabId: string;
        searchText: string;
      }>,
    ) => {
      const { tabId, searchText } = action.payload;
      state.searchText[tabId] = searchText;
    },
    clearSearchText: (
      state,
      action: PayloadAction<{
        tabId: string;
      }>,
    ) => {
      const { tabId } = action.payload;
      state.searchText[tabId] = '';
    },
  },
});

export const { setMetadataForType, setActiveSubTab, setSearchText, clearSearchText } =
  orgMigrationSlice.actions;

export const selectOrgMigrationMetadata =
  (crmOrgId: string, metadataType: string) =>
  (state: RootState): EnrichedConfigurationItem[] | undefined =>
    state.orgMigration.metadataByType[crmOrgId]?.[metadataType];

export const selectOrgMigrationAllMetadata = (crmOrgId: string) => (state: RootState) =>
  state.orgMigration.metadataByType[crmOrgId];

export const selectOrgMigrationActiveSubTab = (tabId: string) => (state: RootState) =>
  state.orgMigration.activeSubTab[tabId];

export const selectOrgMigrationSearchText = (tabId: string) => (state: RootState) =>
  state.orgMigration.searchText[tabId] ?? '';

export default orgMigrationSlice.reducer;
